import React from "react";
import {
	Box,
	FormControl,
	Input,
	Select,
	VStack,
} from "@chakra-ui/react";

import type { FormErrors, MatrimonyFormData } from "src/types/matrimonyForm.types";
import {
	FieldGrid,
	Label,
	SectionHeader,
	commonInputProps,
	commonSelectProps,
	maybeErrorText,
	sectionCardProps,
} from "../matrimonyForm.ui";
import {
	BODY_TYPE_OPTIONS,
	DIETARY_HABITS_OPTIONS,
	DRINKING_HABIT_OPTIONS,
	OWNS_HOUSE_OPTIONS,
	SKIN_TONE_OPTIONS,
	SMOKING_HABIT_OPTIONS,
} from "src/utils/matrimonyOptions";
import { FIELD_LABELS } from "src/utils/matrimonyFieldLabels";

type Props = {
	formData: MatrimonyFormData;
	errors: FormErrors;
	smoking: string;
	setSmoking: (value: string) => void;
	drinking: string;
	setDrinking: (value: string) => void;
	updateField: (field: keyof MatrimonyFormData, value: string) => void;
	renderSegmentedButton: (
		optionValue: string,
		selectedValue: string,
		setSelectedValue: (value: string) => void,
		label: string
	) => React.ReactNode;
};

const Step4PersonalLifestyle: React.FC<Props> = ({
	formData,
	errors,
	updateField,
}) => {
	return (
		<VStack align="stretch" spacing={{ base: 4, md: 6 }}>
			<Box {...sectionCardProps}>
				<SectionHeader title="PHYSICAL ATTRIBUTE" />
				<Box mt={3}>
					<FieldGrid>
						<FormControl
							flex="1"
							isRequired
							isInvalid={Boolean(errors.heightCm)}
						>
							<Label>{FIELD_LABELS.HEIGHT_CM.toUpperCase()}</Label>
							<Input
								{...commonInputProps}
								value={formData.heightCm}
								onChange={(event) =>
									updateField("heightCm", event.target.value)
								}
								placeholder="e.g. 160"
							/>
							{maybeErrorText(errors.heightCm)}
						</FormControl>

						<FormControl flex="1">
							<Label>{FIELD_LABELS.BODY_TYPE.toUpperCase()}</Label>
							<Select
								{...commonSelectProps}
								value={formData.bodyType}
								onChange={(event) =>
									updateField("bodyType", event.target.value)
								}
								placeholder="Select body type"
							>
								{BODY_TYPE_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
						</FormControl>
					</FieldGrid>

					<FormControl mt={4}>
						<Label>{FIELD_LABELS.SKIN_TONE.toUpperCase()}</Label>
						<Select
							{...commonSelectProps}
							value={formData.skinTone}
							onChange={(event) =>
								updateField("skinTone", event.target.value)
							}
							placeholder="Select skin tone"
						>
							{SKIN_TONE_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Select>
					</FormControl>
				</Box>
			</Box>

			<Box {...sectionCardProps}>
				<SectionHeader title="LIFE STYLE" />
				<Box mt={3}>
					<FieldGrid>
						<FormControl flex="1">
							<Label>{FIELD_LABELS.SMOKING_HABIT.toUpperCase()}</Label>
							<Select
								{...commonSelectProps}
								value={formData.smokingHabit || ""}
								onChange={(event) => updateField("smokingHabit", event.target.value)}
								placeholder="Select"
							>
								{SMOKING_HABIT_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
						</FormControl>

						<FormControl flex="1">
							<Label>{FIELD_LABELS.DRINKING_HABIT.toUpperCase()}</Label>
							<Select
								{...commonSelectProps}
								value={formData.drinkingHabit || ""}
								onChange={(event) => updateField("drinkingHabit", event.target.value)}
								placeholder="Select"
							>
								{DRINKING_HABIT_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
						</FormControl>
					</FieldGrid>

					<FieldGrid>
						<FormControl
							flex="1"
							isRequired
							isInvalid={Boolean(errors.dietaryHabits)}
						>
							<Label>{FIELD_LABELS.DIETARY_HABITS.toUpperCase()}</Label>
							<Select
								{...commonSelectProps}
								value={formData.dietaryHabits}
								onChange={(event) =>
									updateField("dietaryHabits", event.target.value)
								}
								placeholder="Select dietary habits"
							>
								{DIETARY_HABITS_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
							{maybeErrorText(errors.dietaryHabits)}
						</FormControl>

						<FormControl flex="1">
							<Label>{FIELD_LABELS.HOBBIES_INTERESTS.toUpperCase()}</Label>
							<Input
								{...commonInputProps}
								value={formData.hobbies}
								onChange={(event) =>
									updateField("hobbies", event.target.value)
								}
								placeholder="e.g. Travelling, Cooking, Reading"
							/>
						</FormControl>
					</FieldGrid>

					<FieldGrid>
						<FormControl flex="1">
							<Label>{FIELD_LABELS.OWNS_HOUSE.toUpperCase()}</Label>
							<Select
								{...commonSelectProps}
								value={formData.ownsHouse || ""}
								onChange={(event) => updateField("ownsHouse", event.target.value)}
								placeholder="Select"
							>
								{OWNS_HOUSE_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
						</FormControl>
					</FieldGrid>
				</Box>
			</Box>
		</VStack>
	);
};

export default Step4PersonalLifestyle;
